import { isRecord } from '@/lib/guards';

import { STORAGE_KEY } from './const';
import {
  clearFilter,
  cloneFiltersDraft,
  compactFiltersDraft,
  createClearedDraft,
  createEmptyDraft,
  removeFilterValue,
  sanitizeFiltersDraft,
} from './parsers';
import { toPersistedPeriodComparison } from './periodComparison';

import type {
  CustomFilterApiKeySet,
  FilterValueSets,
  NewFilters,
  NewFiltersAction,
  NewFiltersPersistedState,
  NewFiltersState,
  NewMultiFilterKey,
  PeriodComparisonSettings,
} from './types';

function getPeriodArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function hasPendingDraftChanges(state: NewFiltersState) {
  return JSON.stringify(state.draft) !== JSON.stringify(state.applied);
}

/**
 * Availability is loaded from the draft, so it only speaks for the applied
 * filters while the draft has no pending edits.
 */
function deselectUnavailableValues(state: NewFiltersState, unavailable: FilterValueSets): NewFiltersState {
  const draft: NewFilters = { ...state.draft };
  let hasDeselected = false;

  for (const [key, values] of Object.entries(unavailable) as Array<[NewMultiFilterKey, ReadonlySet<string>]>) {
    const selected = state.draft[key];
    if (!Array.isArray(selected)) continue;

    const kept = selected.filter((value) => !values.has(String(value)));
    if (kept.length === selected.length) continue;

    draft[key] = kept;
    hasDeselected = true;
  }

  if (!hasDeselected) return state;
  if (hasPendingDraftChanges(state)) return { ...state, draft };

  return { applied: cloneFiltersDraft(draft), draft };
}

/** Reads the persisted new filters payload from localStorage. */
export function loadNewFiltersFromStorage(): NewFiltersPersistedState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Persists draft and applied filter state under the `filters` key. */
export function saveNewFiltersToStorage(
  state: NewFiltersState,
  allowedCustomFilterKeys?: CustomFilterApiKeySet,
  periodComparison?: PeriodComparisonSettings
) {
  if (typeof window === 'undefined') return;

  try {
    const persistedPeriodComparison = periodComparison ? toPersistedPeriodComparison(periodComparison) : undefined;

    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        applied: compactFiltersDraft(state.applied, allowedCustomFilterKeys),
        draft: compactFiltersDraft(state.draft, allowedCustomFilterKeys),
        ...(persistedPeriodComparison ? { periodComparison: persistedPeriodComparison } : {}),
      })
    );
  } catch {
    // Persistence is a convenience; filter state should still work in memory.
  }
}

/** Reducer for all new filters state transitions. */
export function newFiltersReducer(state: NewFiltersState, action: NewFiltersAction): NewFiltersState {
  switch (action.type) {
    case 'applyDraft':
      return { ...state, applied: cloneFiltersDraft(state.draft) };

    case 'clearAppliedFilter':
      return {
        applied: clearFilter(state.applied, action.filter),
        draft: clearFilter(state.draft, action.filter),
      };

    case 'clearDraftFilter':
      return { ...state, draft: clearFilter(state.draft, action.filter) };

    case 'clearEditableDraft':
      return { ...state, draft: createClearedDraft(state.draft, action.options) };

    case 'deselectUnavailableValues':
      return deselectUnavailableValues(state, action.unavailable);

    case 'removeAppliedFilterValue':
      return {
        applied: removeFilterValue(state.applied, action.filter, action.value),
        draft: removeFilterValue(state.draft, action.filter, action.value),
      };

    case 'resetAllFilters': {
      const cleared = cloneFiltersDraft(createClearedDraft(state.applied, action.options));

      return {
        applied: cleared,
        draft: cloneFiltersDraft(cleared),
      };
    }

    case 'resetDraftToApplied':
      return { ...state, draft: cloneFiltersDraft(state.applied) };

    case 'resetToPeriod': {
      const cleared = cloneFiltersDraft({
        ...createEmptyDraft(),
        period: getPeriodArray(action.period),
      });

      return {
        applied: cleared,
        draft: cloneFiltersDraft(cleared),
      };
    }

    case 'setAppliedAndDraftMulti': {
      const nextApplied = cloneFiltersDraft({ ...state.applied, [action.filter]: action.value });

      return {
        applied: nextApplied,
        draft: cloneFiltersDraft({ ...state.draft, [action.filter]: action.value }),
      };
    }

    case 'setAppliedAndDraft': {
      const nextApplied = cloneFiltersDraft({ ...state.applied, ...action.filters });

      return {
        applied: nextApplied,
        draft: cloneFiltersDraft(nextApplied),
      };
    }

    case 'setDraftHazardous':
      return { ...state, draft: { ...state.draft, hazardous: action.value } };

    case 'setDraftMulti':
      return { ...state, draft: { ...state.draft, [action.filter]: action.value } };

    case 'hydrate': {
      const isWrappedState = isRecord(action.state.applied) || isRecord(action.state.draft);
      const fallback =
        isWrappedState && isRecord(action.state.draft)
          ? sanitizeFiltersDraft(action.state.draft, createClearedDraft())
          : createClearedDraft();
      const appliedSource = isWrappedState ? (action.state.applied ?? action.state.draft) : action.state;
      const applied = sanitizeFiltersDraft(appliedSource, fallback);

      return { applied, draft: cloneFiltersDraft(applied) };
    }

    default:
      return state;
  }
}
